import { IconTag } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import type { HomeData } from "../use-home-data";

type ActivePromotionsListProps = {
  promotions: HomeData["activePromotions"];
};

export function ActivePromotionsList({ promotions }: ActivePromotionsListProps) {
  const { t } = useTranslation();

  return (
    <Card className="@container/card flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div>
            <CardTitle>{t("dashboard.promotions.title")}</CardTitle>
            <CardDescription>{t("dashboard.promotions.description")}</CardDescription>
          </div>
          <Button asChild size="sm" variant="outline">
            <Link to="/menu/promotions">{t("dashboard.promotions.manage")}</Link>
          </Button>
        </div>
      </CardHeader>
      {promotions.length === 0 ? (
        <div className="px-6 pb-6 text-muted-foreground text-sm">
          {t("dashboard.promotions.empty")}
        </div>
      ) : (
        <ul className="flex flex-col gap-3 px-6 pb-6">
          {promotions.map((promo) => (
            <li
              className="flex flex-col gap-2 rounded-md border bg-card/50 p-3"
              key={`${promo.kind}:${promo.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <IconTag className="size-4 shrink-0 text-primary" />
                  <span className="truncate font-medium text-sm">{promo.name}</span>
                </div>
                <Badge variant="outline">
                  {t("dashboard.promotions.discount", {
                    value: promo.discount,
                  })}
                </Badge>
              </div>
              {promo.endsInDays !== null && (
                <>
                  <Progress value={promo.progress} />
                  <div className="flex justify-between text-muted-foreground text-xs">
                    <span className="capitalize">
                      {t(`dashboard.promotions.kind.${promo.kind}`)}
                    </span>
                    <span>
                      {promo.endsInDays === 0
                        ? t("dashboard.promotions.endsToday")
                        : t("dashboard.promotions.endsInDays", {
                            count: promo.endsInDays,
                          })}
                    </span>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
